// client/src/components/pages/LexiStar/PlanBoard/PlanBoard.tsx (converted from .jsx, ST-00204)
//
// LexiStar daily plan board (ST-00188 M3, T6-S05; LS-09 §5.2). Route
// /lexistar/plan-board, page permission lexistar.trips.view. Data: GET
// /api/lexistar/plan-board?date&customer&route&material (m3-contracts §7.3).
//
//  - Date stepper ◀ day ▶ on the shared date picker, customer / route /
//    material filters (ThemedSelect); date and filters live in the URL so a
//    view can be shared or put on a wall (LS-09 §1 principle 7).
//  - Map: routes as bold lines coloured by aggregate state, trucks as heading
//    chevrons, source/destination markers with counts, advisories hatched.
//  - Right panel per route: counts, tonnes bar, open deviations, advisories;
//    hover highlights on the map; click expands the trips.
//  - Realtime: any `lexistar:event` for the tenant refetches (debounced), and
//    positions refresh every 30 s while the page is visible.
//  - Wallboard mode (?wall=1) hides the controls and auto-fits.
//
// ST-00204 (phones): the day stepper spans the header row with 44 px ◀ ▶ / Today buttons, and the
// picker opens its one-day bottom sheet (mode="day": one tap picks the day, Yesterday / Today /
// Tomorrow chips). From 640 px with a mouse it is the same control as before.

import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaMapMarkedAlt,
  FaChevronLeft,
  FaChevronRight,
  FaExpand,
  FaCompress,
  FaSyncAlt,
} from 'react-icons/fa';
import UnifiedDateRangePicker from '../../../common/UnifiedDateRangePicker';
import { ThemedSelect, useAuth } from '../../../../types/commonComponents';
import { pickList, listTrips } from '../../../../services/lexistarStatusBoard';
import useLexiStarEvents from '../StatusBoard/useLexiStarEvents';
import {
  PageHeader,
  ErrorBanner,
  NoPermissionPage,
  ConnectionIndicator,
  LoadingState,
  cardClasses,
} from '../StatusBoard/atoms';
import { fmtNum } from '../StatusBoard/format';
import { TONES as TONES_JS } from '../StatusBoard/statusLanguage';
import PlanBoardMapJs from './PlanBoardMap';
import RoutePanel from './RoutePanel';
import { getPlanBoard } from './planBoardApi';
import {
  ROUTE_STATES as ROUTE_STATES_JS,
  sortRoutes,
  boardTotals,
  collectFilterOptions as collectFilterOptionsJs,
  advisoryItems,
  parseDay,
  dayString,
  shiftDay,
  todayString,
  dayLabel,
  DAY_RE,
} from './planBoardLogic';

const LIVE_REFETCH_DEBOUNCE_MS = 2000;
const POSITION_REFRESH_MS = 30000;
const FILTERS = ['customer', 'route', 'material'] as const;
type FilterKey = (typeof FILTERS)[number];

/** A route aggregate on the board (m3-contracts §7.3); the logic module reads the rest. */
interface BoardRoute {
  route_id?: string | number;
  [key: string]: unknown;
}
interface BoardData {
  routes: BoardRoute[];
  sites: unknown[];
}
interface FilterOption {
  value: string;
  label: string;
}
interface FilterOptions {
  customers: FilterOption[];
  materials: FilterOption[];
  routes: FilterOption[];
}
interface BoardTotals {
  routes: number;
  planned: number;
  en_route: number;
  done: number;
  not_started: number;
  tonnes_planned: number;
  tonnes_actual: number;
  open_deviations: number;
  attention: number;
}

const ROUTE_STATES = ROUTE_STATES_JS as Record<string, { label: string; glyph: string; hex: string }>;
const TONES = TONES_JS as unknown as Record<string, { hex: string }>;

// Typed views of the JS modules (their default parameters infer as null / never[]).
const collectFilterOptions = collectFilterOptionsJs as unknown as (
  routes: BoardRoute[],
  previous?: FilterOptions | null,
  trips?: unknown[]
) => FilterOptions;
const PlanBoardMap = PlanBoardMapJs as unknown as ComponentType<{
  routes: BoardRoute[];
  sites: unknown[];
  advisories: unknown[];
  focusId: string | null;
  fitKey: string;
  onSelectRoute: (id: string | number) => void;
  onOpenTrip: (id: string | number) => void;
  className?: string;
}>;

/** Header / toolbar buttons: 44 px on a phone or touch screen, as before with a mouse. */
const stepButton =
  'touch-target p-2 rounded-md border border-slate-300 bg-surface text-slate-600 hover:bg-slate-50';

function Tile({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: string }) {
  return (
    <div className={`${cardClasses} px-4 py-3 min-w-0`} role="group" aria-label={label}>
      <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 leading-tight">
        {label}
      </p>
      <p className={`text-2xl font-bold tabular-nums leading-tight ${tone || 'text-slate-900'}`}>
        {value}
      </p>
      {sub && <p className="text-xs text-slate-500 truncate">{sub}</p>}
    </div>
  );
}

function Legend() {
  const line = (meta: { hex: string }, dashed: boolean) => (
    <span
      className="inline-block w-6"
      style={
        dashed
          ? { borderTop: `3px dashed ${meta.hex}` }
          : { height: 4, background: meta.hex, boxShadow: `0 0 6px ${meta.hex}` }
      }
    />
  );
  return (
    <ul
      className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 px-1 text-[11px] text-slate-600"
      aria-label="Map legend"
    >
      {Object.entries(ROUTE_STATES).map(([key, meta]) => (
        <li key={key} className="inline-flex items-center gap-1.5">
          {line(meta, key === 'not_started')}
          <span aria-hidden="true">{meta.glyph}</span> {meta.label}
        </li>
      ))}
      <li className="inline-flex items-center gap-1.5">
        <span
          className="inline-block w-6 h-2.5"
          style={{
            background: 'repeating-linear-gradient(90deg,#f59e0b 0 4px,#78350f 4px 6px)',
          }}
        />
        ⚠ Advisory
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span style={{ color: TONES.loading?.hex }}>⬒</span> Source
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span style={{ color: '#d97706' }}>⬓</span> Destination
      </li>
      <li className="inline-flex items-center gap-1.5">
        <span style={{ color: TONES.loaded?.hex }}>▲</span> Truck (colour = what it is doing)
      </li>
    </ul>
  );
}

const errorText = (err: unknown): string => {
  const e = err as { response?: { data?: { error?: string } }; message?: string } | null;
  return e?.response?.data?.error || e?.message || 'Failed to load the plan board.';
};

const PlanBoard = () => {
  const { hasPermission } = useAuth();
  const canView = hasPermission('lexistar.trips.view');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const dateParam = params.get('date') || '';
  const day: string = DAY_RE.test(dateParam) ? dateParam : todayString();
  const filters = useMemo(
    () => Object.fromEntries(FILTERS.map((k) => [k, params.get(k) || ''])) as Record<FilterKey, string>,
    [params]
  );
  const wall = params.get('wall') === '1';

  const [data, setData] = useState<BoardData>({ routes: [], sites: [] });
  const [options, setOptions] = useState<FilterOptions>({ customers: [], materials: [], routes: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date | null>(null);
  const [focusId, setFocusId] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  const setParam = useCallback(
    (patch: Record<string, string | null | undefined>) => {
      const next = new URLSearchParams(params);
      Object.entries(patch).forEach(([k, v]) => {
        if (v === null || v === undefined || v === '') next.delete(k);
        else next.set(k, v);
      });
      setParams(next, { replace: true });
    },
    [params, setParams]
  );

  // Customer and material names for the filters: the day's trips carry them on
  // the load (the board filters by them, but does not list them).
  const [dayTrips, setDayTrips] = useState<unknown[]>([]);
  useEffect(() => {
    if (!canView) return undefined;
    let alive = true;
    const d: Date = parseDay(day) ?? new Date();
    const from = new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const to = new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
    listTrips({ from: from.toISOString(), to: to.toISOString(), limit: 500 })
      .then((res: unknown) => alive && setDayTrips(pickList(res, 'trips')))
      .catch(() => alive && setDayTrips([]));
    return () => {
      alive = false;
    };
  }, [canView, day]);
  useEffect(() => {
    if (dayTrips.length) setOptions((prev) => collectFilterOptions([], prev, dayTrips));
  }, [dayTrips]);

  const requestSeq = useRef(0);
  const load = useCallback(
    async ({ quiet = false }: { quiet?: boolean } = {}) => {
      if (!canView) return;
      const seq = ++requestSeq.current;
      if (!quiet) setLoading(true);
      try {
        const res: unknown = await getPlanBoard({ date: day, ...filters });
        if (seq !== requestSeq.current) return;
        const routes = pickList(res, 'routes') as BoardRoute[];
        const sites = pickList(res, 'sites') as unknown[];
        setData({ routes, sites });
        setOptions((prev) => collectFilterOptions(routes, prev));
        setRefreshedAt(new Date());
        setError(null);
      } catch (err) {
        if (seq !== requestSeq.current) return;
        setError(errorText(err));
      } finally {
        if (seq === requestSeq.current) setLoading(false);
      }
    },
    [canView, day, filters]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Live: debounce a refetch on any event for the tenant.
  const debounce = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(debounce.current), []);
  const connection: string = useLexiStarEvents({
    enabled: canView,
    onEvent: () => {
      clearTimeout(debounce.current);
      debounce.current = setTimeout(() => load({ quiet: true }), LIVE_REFETCH_DEBOUNCE_MS);
    },
  });

  // Truck positions are last-known (not events): poll while the page is visible.
  useEffect(() => {
    if (!canView) return undefined;
    const id = setInterval(() => {
      if (typeof document === 'undefined' || document.visibilityState !== 'hidden') {
        load({ quiet: true });
      }
    }, POSITION_REFRESH_MS);
    return () => clearInterval(id);
  }, [canView, load]);

  const routes = useMemo(() => sortRoutes(data.routes) as BoardRoute[], [data.routes]);
  const totals = useMemo(() => boardTotals(routes) as BoardTotals, [routes]);
  const advisories = useMemo(() => advisoryItems(routes) as unknown[], [routes]);
  const fitKey = `${day}|${FILTERS.map((k) => filters[k]).join('|')}|${wall ? 'w' : ''}`;

  const dayPicker = (
    <div className="flex items-center gap-1.5 w-full sm:w-auto" role="group" aria-label="Day">
      <button
        type="button"
        onClick={() => setParam({ date: shiftDay(day, -1) })}
        aria-label="Previous day"
        className={stepButton}
      >
        <FaChevronLeft size={11} />
      </button>
      <div className="flex-1 min-w-0 sm:flex-none sm:w-56">
        <UnifiedDateRangePicker
          startDate={parseDay(day)}
          endDate={parseDay(day)}
          onStartDateChange={(d) => d && setParam({ date: dayString(d) })}
          onEndDateChange={() => {}}
          bounds="all"
          mode="day"
        />
      </div>
      <button
        type="button"
        onClick={() => setParam({ date: shiftDay(day, 1) })}
        aria-label="Next day"
        className={stepButton}
      >
        <FaChevronRight size={11} />
      </button>
      {day !== todayString() && (
        <button
          type="button"
          onClick={() => setParam({ date: null })}
          className="touch-target px-2.5 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-surface text-slate-700 hover:bg-slate-50"
        >
          Today
        </button>
      )}
    </div>
  );

  const header = (
    <PageHeader
      icon={FaMapMarkedAlt}
      title="Daily Plan Board"
      subtitle={dayLabel(day)}
      toolbar={canView && !wall ? dayPicker : null}
      actions={
        canView ? (
          <div className="flex items-center gap-3">
            <ConnectionIndicator status={connection} />
            <button
              type="button"
              onClick={() => load({ quiet: true })}
              aria-label="Refresh"
              title={
                refreshedAt ? `Refreshed ${refreshedAt.toLocaleTimeString('en-GB')}` : 'Refresh'
              }
              className="p-2 rounded-md text-slate-500 hover:bg-slate-100"
            >
              <FaSyncAlt size={12} />
            </button>
            <button
              type="button"
              onClick={() => setParam({ wall: wall ? null : '1' })}
              aria-pressed={wall}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 bg-surface text-slate-700 hover:bg-slate-50"
            >
              {wall ? <FaCompress size={11} /> : <FaExpand size={11} />}
              {wall ? 'Exit wallboard' : 'Wallboard'}
            </button>
          </div>
        ) : null
      }
    />
  );

  if (!canView) {
    return (
      <NoPermissionPage
        header={header}
        message="You do not have permission to view the plan board."
      />
    );
  }

  const filterSelect = (key: FilterKey, label: string, opts: FilterOption[]) => (
    <div className="w-full sm:w-56" key={key}>
      <label htmlFor={`pb-filter-${key}`} className="sr-only">
        {label}
      </label>
      <ThemedSelect
        id={`pb-filter-${key}`}
        aria-label={label}
        value={filters[key]}
        onChange={(e) => setParam({ [key]: String(e.target.value) })}
        isClearable
        className="text-sm"
      >
        <option value="">{`All ${label.toLowerCase()}s`}</option>
        {opts.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </ThemedSelect>
    </div>
  );

  const anyFilter = FILTERS.some((k) => filters[k]);

  return (
    <div className="min-h-screen bg-slate-50">
      {header}
      <div className="max-w-[1800px] mx-auto px-3 sm:px-6 py-5 space-y-4">
        <ErrorBanner message={error} onDismiss={() => setError(null)} />

        {!wall && (
          <div
            className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2"
            role="search"
            aria-label="Filters"
          >
            {filterSelect('customer', 'Customer', options.customers)}
            {filterSelect('route', 'Route', options.routes)}
            {filterSelect('material', 'Material', options.materials)}
            {anyFilter && (
              <button
                type="button"
                onClick={() => setParam({ customer: null, route: null, material: null })}
                className="text-sm font-medium text-primary-700 hover:underline px-2"
              >
                Clear filters
              </button>
            )}
          </div>
        )}

        {!wall && (
          <div
            className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-7 gap-3"
            aria-label="Day totals"
          >
            <Tile
              label="Routes"
              value={fmtNum(totals.routes)}
              sub={`${totals.attention} need attention`}
            />
            <Tile label="Trips planned" value={fmtNum(totals.planned)} />
            <Tile label="En route" value={fmtNum(totals.en_route)} tone="text-blue-800" />
            <Tile label="Done" value={fmtNum(totals.done)} />
            <Tile label="Not started" value={fmtNum(totals.not_started)} tone="text-slate-500" />
            <Tile
              label="Tonnes"
              value={fmtNum(totals.tonnes_actual)}
              sub={`of ${fmtNum(totals.tonnes_planned)} t planned`}
            />
            <Tile
              label="Open deviations"
              value={fmtNum(totals.open_deviations)}
              tone={totals.open_deviations ? 'text-pink-800' : 'text-slate-900'}
            />
          </div>
        )}

        {loading && data.routes.length === 0 ? (
          <LoadingState label="Loading the day's plan..." />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
            <div className="lg:col-span-8 xl:col-span-9">
              <PlanBoardMap
                routes={routes}
                sites={data.sites}
                advisories={advisories}
                focusId={focusId ?? expandedId}
                fitKey={fitKey}
                onSelectRoute={(id: string | number) => setExpandedId(String(id))}
                onOpenTrip={(id: string | number) => navigate(`/lexistar/trips/${id}`)}
                className={`relative z-0 rounded-xl overflow-hidden border border-slate-200 ${wall ? 'h-[calc(100vh-150px)] min-h-[480px]' : 'h-[420px] lg:h-[600px]'}`}
              />
              <Legend />
            </div>
            <section
              className={`${cardClasses} lg:col-span-4 xl:col-span-3 overflow-hidden flex flex-col ${wall ? 'lg:max-h-[calc(100vh-150px)]' : 'lg:max-h-[640px]'}`}
              aria-label="Routes panel"
            >
              <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
                <h2 className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                  Routes {day === todayString() ? 'today' : `on ${dayLabel(day)}`}
                </h2>
                <span className="text-xs text-slate-500 tabular-nums">{routes.length}</span>
              </div>
              <div className="overflow-y-auto">
                <RoutePanel
                  routes={routes}
                  expandedId={expandedId}
                  onToggle={(id: string) => setExpandedId((cur) => (cur === id ? null : id))}
                  onFocus={setFocusId}
                  compact={wall}
                />
              </div>
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlanBoard;
